type ValueMetric = {
  value?: number | null;
};

type FloorMetric = {
  floor?: string | null;
  electricity?: number | null;
};

export type C1TotalEnergyResponse = {
  type: 'Q1';
  totalEnergy?: ValueMetric | null;
  energyBreakdown?: {
    electricity?: (ValueMetric & { rawValue?: number | null }) | null;
    gas?: (ValueMetric & { rawValue?: number | null }) | null;
  } | null;
  waterUsage?: number | null;
};

export type C1CarbonEmissionResponse = {
  type: 'Q2';
  month?: string | null;
  totalCarbon?: ValueMetric | null;
  carbonBreakdown?: {
    gridElectricity?: (ValueMetric & { rawValue?: number | null; factor?: number | null }) | null;
    gas?: (ValueMetric & { rawValue?: number | null; factor?: number | null }) | null;
    pv?: (ValueMetric & { rawValue?: number | null; factor?: number | null }) | null;
  } | null;
  baselineComparison?: {
    baselineCarbon?: number | null;
    actualCarbon?: number | null;
    reduction?: number | null;
    reductionRate?: number | null;
  } | null;
};

export type C1HvacElectricityResponse = {
  type: 'Q3';
  totalHvacElectricity?: ValueMetric | null;
  floorRanking?: FloorMetric[] | null;
  highestFloor?: FloorMetric | null;
  lowestFloor?: FloorMetric | null;
  rate?: number | null;
};

export type C1FloorElectricityResponse = {
  type: 'Q4';
  month?: string | null;
  floorRanking?: Array<FloorMetric & { unit?: string | null; percentage?: number | null }> | null;
  averageElectricity?: number | null;
  highestFloor?: FloorMetric | null;
  lowestFloor?: FloorMetric | null;
  totalFloors?: number | null;
};

export type C1BenchmarkResponse = {
  type: 'Q5';
  area?: number | null;
  unitAreaEnergy?: ValueMetric | null;
  unitAreaCarbon?: ValueMetric | null;
  benchmarkTable?: {
    energy?: {
      constraintValue?: number | null;
      baselineValue?: number | null;
      guideValue?: number | null;
    } | null;
  } | null;
};

export type C1BreakdownResponse = {
  type: 'Q6';
  carbonBreakdown?: Array<{
    category?: string | null;
    carbon?: number | null;
    rawElectricity?: number | null;
    rawGas?: number | null;
    percentage?: number | null;
  }> | null;
  mainEnergySource?: {
    category?: string | null;
    value?: number | null;
    percentage?: number | null;
  } | null;
  mainCarbonSource?: {
    category?: string | null;
    value?: number | null;
    percentage?: number | null;
  } | null;
};

export type C1Response =
  | C1TotalEnergyResponse
  | C1CarbonEmissionResponse
  | C1HvacElectricityResponse
  | C1FloorElectricityResponse
  | C1BenchmarkResponse
  | C1BreakdownResponse;

const C1_RESPONSE_TYPES = new Set<C1Response['type']>(['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6']);

export const parseC1Response = (value: unknown): C1Response | null => {
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || !C1_RESPONSE_TYPES.has(parsed.type)) {
      return null;
    }
    return parsed as C1Response;
  } catch {
    return null;
  }
};
